import { useState } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { SearchRecord, ThreadResult } from '@weave/schema-gen-ts/dist/schemas/sms/search/v2/sms_search_service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { PersonsV3 } from '@frontend/api-person';
import { SMSSearchV2Mutations, SMSSearchV2Queries } from '@frontend/api-sms-search-v2';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useGlobalSearch } from '@frontend/global-search';
import { useTranslation } from '@frontend/i18n';
import { HiddenFilterKeys, InboxFilters } from '@frontend/inbox-filters';
import { sanitizePhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { PERSON_SEARCH_PAGE_SIZE } from '../constants';
import { containsNoAlphabets } from '../utils';

type SearchResults<T> = {
  isLoading: boolean;
  results: T[];
  total: number;
};

type InfiniteSearchResults<T> = Omit<SearchResults<T>, 'isLoading'> & {
  isLoading: {
    firstPage: boolean;
    nextPage: boolean;
  };
  hasNextPage: boolean;
  loadMore: () => void;
};

type UseInboxSearchResults = {
  recentSearches: SearchResults<SearchRecord>;
  personsQuery: InfiniteSearchResults<Person>;
  messagesQuery: InfiniteSearchResults<ThreadResult>;
  filters: InboxFilters;
  setFilters: (newVal: InboxFilters) => void;
  hideFilters?: HiddenFilterKeys;
  removeSearchResult: (searchId: string) => void;
  logSearch: (searchQuery: string) => void;
};

export const useInboxSearch = (): UseInboxSearchResults => {
  const { t } = useTranslation('inbox');
  const alert = useAlert();
  const { selectedLocationIds } = useAppScopeStore();
  const [filters, setFilters] = useState<InboxFilters>({
    tags: [],
    locationPhones: [],
    statuses: [],
    startTime: '',
    startDate: '',
    endTime: '',
    endDate: '',
    tasks: [],
    aiReceptionist: [],
    conversationType: '',
  });
  const { debouncedSearchTerm } = useGlobalSearch(['debouncedSearchTerm']);
  const isPhoneNumberSearch = containsNoAlphabets(debouncedSearchTerm);
  const locationIdsWithACL = filterLocationIdsByACLs({
    locationIds: selectedLocationIds,
    acls: Permission.SMS_INBOX_MANAGE,
  });

  const recentSearchesQuery = SMSSearchV2Queries.useListSearchesQuery({});
  const removeRecentSearchMutation = SMSSearchV2Mutations.useDeleteSearchMutation({
    onSuccess: () => {
      alert.success(t('Search successfully deleted.'));
    },
    onError: () => {
      alert.error(t('Something went wrong. Please try again later.'));
    },
  });
  const logSearchMutation = SMSSearchV2Mutations.useLogSearchMutation();

  const personsQuery = PersonsV3.PersonQueries.usePersonSearchLegacyInfiniteQuery(
    {
      locationIds: locationIdsWithACL,
      search: debouncedSearchTerm,
      page: {
        size: PERSON_SEARCH_PAGE_SIZE,
      },
    },
    {
      enabled: !!debouncedSearchTerm && !isPhoneNumberSearch,
      retry: false,
      select: (data) => {
        return {
          ...data,
          pages: data.pages.map((page) =>
            page.persons
              ? page
              : {
                  ...page,
                  persons: [],
                }
          ),
        };
      },
      onError: (err) => {
        if (
          typeof err.data === 'object' &&
          typeof err.data.message === 'string' &&
          err.data.message.includes('multiple org ids found')
        ) {
          alert.error(
            t('Contacts could not be searched. Please ensure you have only one organization selected, and try again.')
          );
        }
      },
    }
  );

  const personsByPhoneQuery = PersonsV3.PersonQueries.useListPersonsByPhoneLegacyInfiniteQuery(
    {
      locationIds: locationIdsWithACL,
      phoneNumber: sanitizePhoneNumber(debouncedSearchTerm),
      partialSearch: true,
      page: {
        size: PERSON_SEARCH_PAGE_SIZE,
      },
    },
    {
      enabled: !!debouncedSearchTerm && isPhoneNumberSearch,
      retry: false,
      select: (data) => {
        return {
          ...data,
          pages: data.pages.map((page) =>
            page.persons
              ? page
              : {
                  ...page,
                  persons: [],
                }
          ),
        };
      },
      onError: (err) => {
        if (
          typeof err.data === 'object' &&
          typeof err.data.message === 'string' &&
          err.data.message.includes('multiple org ids found')
        ) {
          alert.error(
            t('Contacts could not be searched. Please ensure you have only one organization selected, and try again.')
          );
        }
      },
    }
  );

  const messagesQuery = SMSSearchV2Queries.useSMSSearchV2InfiniteQuery(
    {
      locationId: locationIdsWithACL[0] ?? '',
      groupIds: locationIdsWithACL,
      query: debouncedSearchTerm,
      tagIds: filters.tags.length ? filters.tags : undefined,
      pageSize: 25,
    },
    {
      enabled: !!debouncedSearchTerm && !!locationIdsWithACL[0],
      retry: 1,
    }
  );

  const personsResults: Person[] = personsQuery.data?.pages.flatMap((page) => page.persons ?? []) ?? [];
  const personsByPhoneResults: Person[] = personsByPhoneQuery.data?.pages.flatMap((page) => page.persons ?? []) ?? [];
  const messagesResults: ThreadResult[] = messagesQuery.data?.pages.flatMap((page) => page.threads) ?? [];

  const baseHideFilters: HiddenFilterKeys = [
    'time',
    'statuses',
    'date',
    'endDate',
    'startTime',
    'startDate',
    'endTime',
  ];

  return {
    recentSearches: {
      isLoading: recentSearchesQuery.isLoading,
      results: recentSearchesQuery.data?.searches ?? [],
      total: recentSearchesQuery.data?.searches.length ?? 0,
    },
    removeSearchResult: (searchId: string) => {
      removeRecentSearchMutation.mutate({ searchId });
    },
    logSearch: (searchQuery: string) => {
      logSearchMutation.mutate({ query: searchQuery });
    },
    personsQuery: isPhoneNumberSearch
      ? {
          isLoading: {
            firstPage: personsByPhoneQuery.isLoading,
            nextPage: personsByPhoneQuery.isFetchingNextPage,
          },
          loadMore: personsByPhoneQuery.fetchNextPage,
          hasNextPage: !!personsByPhoneQuery.hasNextPage,
          results: personsByPhoneResults,
          total: personsByPhoneResults.length,
        }
      : {
          isLoading: {
            firstPage: personsQuery.isLoading,
            nextPage: personsQuery.isFetchingNextPage,
          },
          loadMore: personsQuery.fetchNextPage,
          hasNextPage: !!personsQuery.hasNextPage,
          results: personsResults,
          total: personsResults.length,
        },
    messagesQuery: {
      isLoading: {
        firstPage: messagesQuery.isLoading,
        nextPage: messagesQuery.isFetchingNextPage,
      },
      loadMore: messagesQuery.fetchNextPage,
      hasNextPage: !!messagesQuery.hasNextPage,
      results: messagesResults,
      total: messagesResults.length,
    },
    filters,
    setFilters,
    hideFilters: [...baseHideFilters, 'locationPhones'],
  };
};
